import React from 'react';

// Client-facing summary of a Scoped Project proposal: hourly work in
// workstreams, each bounded by a floor (minimum fee) and a cap (maximum
// in-scope fee); the deposit covers the whole project. The detailed scope
// lives in the contract package the client signs after accepting.
const fmtMoney = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

export default function ScopedProjectSummary({ data }) {
  const d = data || {};
  const streams = Array.isArray(d.workstreams) && d.workstreams.length > 0
    ? d.workstreams
    : (d.floor || d.ceiling ? [{ sub_tag: d.tag || 'Project', floor: d.floor, ceiling: d.ceiling }] : []);
  const floorSum = streams.reduce((s, w) => s + (Number(w.floor) || 0), 0);
  const ceilingSum = streams.reduce((s, w) => s + (Number(w.ceiling) || 0), 0);
  const msList = Array.isArray(d.milestones) ? d.milestones : [];
  const atSigning = msList.filter(m => m.due_at_signing)
    .reduce((s, m) => s + (Number(m.amount) || 0), 0);
  const deposit = d.deposit_mode === 'none'
    ? 'No deposit required'
    : d.deposit_mode === 'custom'
      ? `${fmtMoney(d.deposit_amount)} deposit, due before work begins`
      : d.deposit_mode === 'milestones'
        ? `Milestone payments per the schedule below${atSigning > 0 ? ` — ${fmtMoney(atSigning)} due at signing` : ''}`
        : `${fmtMoney(floorSum)} deposit (the combined project minimum), due before work begins`;

  const cell = { padding: '9px 6px', borderTop: '1px solid #e2e8f0' };

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '20px 24px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 17 }}>{d.tag || 'Project Proposal'}</h3>
      {d.scope_description && (
        <p style={{ fontSize: 14, color: '#475569', whiteSpace: 'pre-wrap', marginTop: 8 }}>
          {d.scope_description}
        </p>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 14, fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12 }}>
            <th style={{ paddingBottom: 6 }}>Workstream</th>
            <th style={{ paddingBottom: 6, textAlign: 'right' }}>Minimum fee (floor)</th>
            <th style={{ paddingBottom: 6, textAlign: 'right' }}>Maximum fee (cap)</th>
          </tr>
        </thead>
        <tbody>
          {streams.map((w, i) => (
            <tr key={i}>
              <td style={cell}>{w.sub_tag}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{fmtMoney(w.floor)}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{fmtMoney(w.ceiling)}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 700 }}>
            <td style={cell}>Combined project</td>
            <td style={{ ...cell, textAlign: 'right' }}>{fmtMoney(floorSum)}</td>
            <td style={{ ...cell, textAlign: 'right' }}>{fmtMoney(ceilingSum)}</td>
          </tr>
          <tr>
            <td style={cell}>Hourly rate</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }} colSpan={2}>{fmtMoney(d.hourly_rate)} / hour</td>
          </tr>
          <tr>
            <td style={cell}>Deposit</td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }} colSpan={2}>{deposit}</td>
          </tr>
        </tbody>
      </table>
      {d.deposit_mode === 'milestones' && msList.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12 }}>
              <th style={{ paddingBottom: 4 }}>Payment schedule</th>
              <th style={{ paddingBottom: 4, textAlign: 'right' }}>Amount</th>
              <th style={{ paddingBottom: 4, textAlign: 'right' }}>When</th>
            </tr>
          </thead>
          <tbody>
            {msList.map((m, i) => (
              <tr key={i}>
                <td style={cell}>{m.label}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmtMoney(m.amount)}</td>
                <td style={{ ...cell, textAlign: 'right', color: '#475569' }}>
                  {m.due_at_signing ? 'At signing' : 'At milestone completion'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p style={{ fontSize: 13, color: '#64748b', marginTop: 14 }}>
        Project hours are billed against the deposit at the hourly rate above,
        tracked separately per workstream. Each workstream's total will never
        be less than its minimum fee or more than its cap. After accepting,
        you'll receive the project agreement and scope of work to review and sign.
      </p>
    </div>
  );
}
